import { themeZafire } from '@zafire/ag-grid-enterprise';
import type { ColDef } from 'ag-grid-enterprise';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridTable } from '../../../app/components/atoms';
import { PRIMARY_NAVIGATION_BAR_HEIGHT } from '../../../app/config/constants';
import { useLayout } from '../../../app/contexts/layout-context';
import { useYwuApp } from '../../../app/contexts/ywu-app-context';
import { formatAmountCurrency } from '../../../app/utils/renderUtils';
import { useUpdateLiquidityCalculatorCalculation } from '../../api';
import type { ILiquidityCalculationValues, ILiquidityCalculatorTableRow, LiquidityCalculatorSettingsSection } from '../../interfaces';
import { LiquidityCalculatorAmountCell } from './liquidity-calculator-amount-cell';
import { LiquidityCalculatorAmountInput } from './liquidity-calculator-amount-input';
import { LiquidityCalculatorPositionNameCell } from './liquidity-calculator-position-name-cell';

// Liquidity-planner header (154) + content-box vertical margin & padding (48 + 48) + calculator footer (button 36 + padding 24 + border 1)
const LIQUIDITY_CALCULATOR_VIEWPORT_RESERVE_BELOW_NAVBAR_PX = 154 + 48 + 48 + 61;

interface ILiquidityCalculatorTableProps {
    calculationData: ILiquidityCalculationValues;
    isStackedLayout: boolean;
    onOpenSettingsDrawer: (section: LiquidityCalculatorSettingsSection) => void;
    onOpenNeedDetails: () => void;
}

export const LiquidityCalculatorTable: FunctionComponent<ILiquidityCalculatorTableProps> = ({
    calculationData,
    isStackedLayout,
    onOpenSettingsDrawer,
    onOpenNeedDetails,
}) => {
    const { t } = useTranslation();
    const { totalNavbarHeight } = useLayout();
    const { isWealthNavigatorMFE } = useYwuApp();
    const updateLiquidityCalculatorCalculation = useUpdateLiquidityCalculatorCalculation();

    const updateCashBuffer = useCallback(
        (cashBuffer: number) => {
            if (calculationData.calculatorPreferencesId == null) return;

            updateLiquidityCalculatorCalculation.mutate({ id: calculationData.calculatorPreferencesId, cashBuffer: cashBuffer });
        },
        [calculationData.calculatorPreferencesId, updateLiquidityCalculatorCalculation],
    );

    const liquidityCalculatorTableColDefs: ColDef<ILiquidityCalculatorTableRow>[] = [
        {
            field: 'positionName',
            headerName: t('liquidityCalculatorTable.column.positionName'),
            flex: 1,
            cellRenderer: LiquidityCalculatorPositionNameCell,
        },
        {
            field: 'amount',
            headerName: t('liquidityCalculatorTable.column.amount'),
            minWidth: 250,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            editable: ({ data }) => !!data?.isAmountEditable,
            cellRenderer: LiquidityCalculatorAmountCell,
            cellRendererParams: { currency: calculationData.currency, onOpenSettingsDrawer, onOpenNeedDetails },
            cellEditor: LiquidityCalculatorAmountInput,
            cellEditorParams: { currency: calculationData.currency },
            onCellValueChanged: ({ data, newValue, oldValue }) => {
                if (data?.isAmountEditable && newValue !== oldValue) data.onAmountChange?.(Number(newValue));
            },
            valueFormatter: ({ value }) => formatAmountCurrency(value, calculationData.currency) || '0',
        },
    ];

    const rowData: ILiquidityCalculatorTableRow[] = [
        {
            positionName: t('liquidityCalculatorTable.position.cashShortTermJb'),
            amount: calculationData.cashWithJb.amount,
            isEnabled: calculationData.cashWithJb.isEnabled,
            showEdit: true,
            settingsSection: 'cashWithJb',
        },
        {
            positionName: t('liquidityCalculatorTable.position.cashShortTermOtherBanks'),
            amount: calculationData.cashWithOtherBanks.amount,
            isEnabled: calculationData.cashWithOtherBanks.isEnabled,
            showEdit: true,
            settingsSection: 'cashWithOtherBanks',
        },
        {
            positionName: t('liquidityCalculatorTable.position.totalCash'),
            amount: calculationData.totalSelectedCash,
            percentage: calculationData.totalSelectedCashPercentage,
            isTotal: true,
        },
        {
            positionName: t('liquidityCalculatorTable.position.liquidityNeed'),
            amount: calculationData.liquidityNeed.amount,
            isEnabled: calculationData.liquidityNeed.isEnabled,
            showEdit: true,
            showNeedDetails: true,
            settingsSection: 'cashflows',
        },
        {
            positionName: t('liquidityCalculatorTable.position.cashBuffer'),
            amount: calculationData.cashBuffer,
            isAmountEditable: calculationData.calculatorPreferencesId != null,
            onAmountChange: updateCashBuffer,
        },
        {
            positionName: t('liquidityCalculatorTable.position.totalLiquidity', {
                dateRange: t(`liquidityCalculator.dateRange.${calculationData.scopeRange}`),
            }),
            amount: calculationData.totalLiquidity,
            percentage: calculationData.totalLiquidityPercentage,
            isTotal: true,
        },
        {
            positionName: t('liquidityCalculatorTable.position.availableInvestmentAmount'),
            amount: calculationData.availableInvestmentAmount,
            percentage: calculationData.availableInvestmentPercentage,
            isTotal: true,
            isHighlighted: true,
        },
    ];

    const navHeight = isWealthNavigatorMFE ? PRIMARY_NAVIGATION_BAR_HEIGHT : totalNavbarHeight;
    const screenHeaderPaddingsAndHeight = navHeight + LIQUIDITY_CALCULATOR_VIEWPORT_RESERVE_BELOW_NAVBAR_PX;

    return (
        <AgGridTable<ILiquidityCalculatorTableRow>
            addTableContainer
            theme={themeZafire.withZafire('density', 'comfort').withParams({ wrapperBorder: false })}
            rowData={rowData}
            columnDefs={liquidityCalculatorTableColDefs}
            defaultColDef={{
                sortable: false,
                resizable: false,
                suppressHeaderMenuButton: true,
                cellStyle: { display: 'flex', alignItems: 'center' },
            }}
            getRowStyle={({ data }) => ({
                ...(data?.isTotal ? { borderBottom: '2px solid var(--border-color-default-emphasis)' } : {}),
                ...(data?.isHighlighted ? { backgroundColor: 'var(--zf-table-ag-grid-odd-row-background-color)' } : {}),
            })}
            screenHeaderPaddingsAndHeight={screenHeaderPaddingsAndHeight}
            fitContentHeight={isStackedLayout}
            headerRowHeight={45}
            tableRowHeight={56}
            tableContainerPadding={0}
        />
    );
};
